import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Keyboard, Sparkles, ListFilter, ChevronUp, ChevronDown, Tag, Star, Archive, ArchiveRestore, Mail, MailOpen, X, Clock, BellOff } from 'lucide-react'
import ReactDOM from 'react-dom'
import SvgFragment from '../../../../../fragments/SvgFragment'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import { useAppHandlers } from '../../../../../services/actions/app/useAppHandlers'
import { buildBulkHubspotExportItems } from '../hubspotInboxExportUtils'
import { useInboxModals } from '../useInboxModals'
import { PinUnpinFilter, StarredFilter, ReadUnreadFilter, ArchiveFilter, TagsFilter, LastMessagedFilter } from './InboxFilters'
import TooltipWrapper from '../../../../../components/TooltipWrapper'
import SnoozeMenu from '../ConversationList/SnoozeMenu'
import { useIsSharedInbox } from '../../../../../stores/inbox/InboxModeContext'

const INBOX_TOOLS_ONBOARDING_KEY = 'inbox_tools_onboarding_closed'

const PinIcon: React.FC<{ active?: boolean }> = ({ active = false }) => {
    if (active) {
        return (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <g transform="translate(8 8) scale(1.12) translate(-8 -8)">
                    <path
                        fill="currentColor"
                        fillRule="evenodd"
                        d="m.837 14.845.533-1.598a.298.298 0 0 1 .06-.098l2.309-2.308L.72 7.822a.753.753 0 0 1 0-1.065c1.136-1.136 2.871-1.186 4.432-.616l2.896-2.353-.937-.937a.753.753 0 0 1 0-1.065L8.178.72a.753.753 0 0 1 1.065 0l6.036 6.036a.753.753 0 0 1 0 1.065l-1.065 1.066a.753.753 0 0 1-1.065 0l-.937-.937-2.353 2.896c.576 1.577.51 3.306-.616 4.432a.753.753 0 0 1-1.065 0l-3.019-3.018-2.308 2.308a.251.251 0 0 1-.098.061l-1.598.533a.251.251 0 0 1-.318-.318z"
                    />
                </g>
            </svg>
        )
    }

    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path
                d="M4.90781 10.5922L0.75 14.75M7.34633 3.99587L6.19907 5.14313C6.10549 5.23671 6.05869 5.2835 6.00538 5.32069C5.95806 5.35369 5.90702 5.381 5.85332 5.40207C5.79281 5.4258 5.72792 5.43878 5.59814 5.46474L2.90474 6.00342C2.2048 6.14341 1.85482 6.2134 1.69109 6.39793C1.54845 6.55868 1.48332 6.77381 1.51283 6.98669C1.54671 7.23104 1.79908 7.48341 2.30382 7.98815L7.51186 13.1962C8.0166 13.701 8.26897 13.9533 8.51333 13.9872C8.7262 14.0167 8.94133 13.9516 9.10209 13.8089C9.28661 13.6452 9.35661 13.2952 9.4966 12.5953L10.0353 9.90188C10.0612 9.7721 10.0742 9.70722 10.0979 9.64671C10.119 9.593 10.1463 9.54196 10.1793 9.49464C10.2165 9.44133 10.2633 9.39454 10.3569 9.30095L11.5041 8.15369C11.564 8.09386 11.5939 8.06394 11.6268 8.03782C11.656 8.01462 11.6869 7.99367 11.7193 7.97516C11.7558 7.95433 11.7947 7.93766 11.8725 7.90433L13.7058 7.1186C14.2407 6.88937 14.5081 6.77476 14.6296 6.58955C14.7358 6.42759 14.7738 6.23024 14.7353 6.04041C14.6913 5.82333 14.4856 5.61759 14.0741 5.20611L10.2939 1.42587C9.88242 1.0144 9.67669 0.808661 9.45961 0.764655C9.26977 0.726173 9.07243 0.764179 8.91046 0.870411C8.72525 0.99189 8.61064 1.25932 8.38141 1.79419L7.59569 3.62755C7.56235 3.70533 7.54569 3.74422 7.52486 3.78068C7.50635 3.81308 7.4854 3.84402 7.4622 3.87323C7.43608 3.90612 7.40616 3.93603 7.34633 3.99587Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

interface IInboxControlBarProps {
    setFiltersHeight?: (h: number) => void
    onShowShortcuts?: () => void
    onOpenTrainAiModal?: () => void
    isTrainingAi?: boolean
    /** When false, toolbar onboarding bubbles stay hidden (e.g. until Train AI intro modal is closed). */
    allowToolbarOnboarding?: boolean
}

const InboxControlBar: React.FC<IInboxControlBarProps> = ({
    setFiltersHeight,
    onShowShortcuts,
    onOpenTrainAiModal,
    isTrainingAi = false,
    allowToolbarOnboarding = true,
}) => {
    const [filtersOpened, setFiltersOpened] = useState(true)
    const [moreOpen, setMoreOpen] = useState(false)
    const [snoozeAnchor, setSnoozeAnchor] = useState<{ top: number; right?: number; left?: number } | null>(null)
    const [unsnoozeConfirmOpen, setUnsnoozeConfirmOpen] = useState(false)
    const [onboardingStep, setOnboardingStep] = useState<'shortcuts' | 'train-ai' | null>(null)
    const controlBarRef = useRef<HTMLDivElement>(null)
    const moreRef = useRef<HTMLDivElement>(null)
    const snoozeItemRef = useRef<HTMLButtonElement>(null)

    const { store: { selectedConversations, selectedConversation, numberOfFilters, fetchingConversations, pinned, starred, readed, archived, tags, lastMessagedFrom, lastMessagedTo, conversationStatus, lastMessagedOnlyLast, conversationOwners, selectedTab, activeInboxLabel } } = useInboxStore()
    const { store: { workspace } } = useAuthStore()
    const {
        starConversationsHandler,
        pinConversationsHandler,
        readConversationsHandler,
        starConversationHandler,
        pinConversationHandler,
        readConversationHandler,
        archiveConversationsHandler,
        snoozeConversationsHandler,
        openNewMessagePage,
        resetFilters,
        setResetSelectedConversations,
        setResetLastFetchedConversation,
        updateNumberOfFilters,
    } = useInboxHandlers()
    const { setBulkEditTagsModal } = useAppHandlers()
    const { openHubspotExportModal } = useInboxModals()

    const hubspotEnabled = workspace?.hubspotEnabled === true
    const hasSelected = selectedConversations.length > 0
    const allSelectedRead = hasSelected && selectedConversations.every((c: any) => c.isRead)
    const allSelectedUnread = hasSelected && selectedConversations.every((c: any) => !c.isRead)
    const isSharedInbox = useIsSharedInbox()
    /** Only the personal SN tab is out of reach of these actions; shared SN threads are ordinary rows. */
    const isSnTab = selectedTab === 'SalesNavigator' && !isSharedInbox
    /**
     * With nothing ticked, Star / Pin / Tag / Read act on the conversation that is open (and
     * toggle, since its state is known). They used to sit greyed out next to an open thread,
     * which read as "these do not work".
     */
    const openTarget = !hasSelected ? selectedConversation : null
    const tagTargets = hasSelected ? selectedConversations : openTarget ? [openTarget] : []
    const canTag = tagTargets.some((c: any) => c.connectionId)

    useEffect(() => {
        updateNumberOfFilters()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pinned, starred, readed, archived, tags.length, lastMessagedFrom, lastMessagedTo, conversationStatus?.value, lastMessagedOnlyLast, conversationOwners, selectedTab])

    useEffect(() => {
        if (controlBarRef.current && setFiltersHeight) {
            setFiltersHeight(controlBarRef.current.clientHeight)
        }
    })

    useEffect(() => {
        if (!allowToolbarOnboarding || isSharedInbox) return
        if (localStorage.getItem(INBOX_TOOLS_ONBOARDING_KEY) === 'true') return
        setOnboardingStep('train-ai')
    }, [allowToolbarOnboarding, isSharedInbox])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
                setMoreOpen(false)
            }
        }
        if (moreOpen) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [moreOpen])

    const resetAllFilters = () => {
        resetFilters()
        setResetSelectedConversations()
        setResetLastFetchedConversation()
    }

    const handleTagConversations = () => {
        const connectionIds = tagTargets.map((c: any) => c.connectionId).filter(Boolean)
        if (!connectionIds.length) return
        const connectionTagSnapshots = tagTargets
            .filter((c: any) => c.connectionId)
            .map((c: any) => ({ connectionId: String(c.connectionId), tags: c.tags || [] }))
        setBulkEditTagsModal(connectionIds, false, connectionTagSnapshots, { closeOnBackdropClick: true })
    }

    const handleHubspotExport = () => {
        if (!selectedConversations.length) return
        openHubspotExportModal({
            bulk: true,
            isSales: isSnTab,
            items: buildBulkHubspotExportItems(selectedConversations, isSnTab),
        })
    }

    const closeOnboarding = () => {
        localStorage.setItem(INBOX_TOOLS_ONBOARDING_KEY, 'true')
        setOnboardingStep(null)
    }

    const showNextOnboardingStep = () => {
        setOnboardingStep('shortcuts')
    }

    const showPreviousOnboardingStep = () => {
        setOnboardingStep('train-ai')
    }

    return (
        <Container ref={controlBarRef}>
            <TopRow>
                <LeftControls>
                    {/* Shared inbox answers existing threads; new outreach starts from your own inbox or a campaign. */}
                    {!isSharedInbox && (
                        <TooltipWrapper tooltipText="New Message (C)" placement="top">
                            <PrimaryBtn onClick={() => openNewMessagePage()} disabled={fetchingConversations || isSnTab}>
                                <PlusSign>+</PlusSign> New Message
                            </PrimaryBtn>
                        </TooltipWrapper>
                    )}

                    <SecondaryBtn disabled={!hasSelected || !hubspotEnabled || fetchingConversations} onClick={handleHubspotExport}>
                        <SvgFragment type="exportHubspot" /> Export to HubSpot
                    </SecondaryBtn>

                    <SecondaryBtn
                        disabled={(!hasSelected && !openTarget) || fetchingConversations || isSnTab}
                        onClick={() => openTarget
                            ? starConversationHandler({ conversationId: openTarget.conversationId, isStarred: !openTarget.isStarred })
                            : starConversationsHandler(true)}
                    >
                        <Star size={12} fill={openTarget?.isStarred ? '#FDBE00' : 'none'} color={openTarget?.isStarred ? '#FDBE00' : 'currentColor'} />
                        {openTarget?.isStarred ? 'Unstar' : 'Star'}
                    </SecondaryBtn>

                    <SecondaryBtn
                        disabled={(!hasSelected && !openTarget) || fetchingConversations || isSnTab}
                        onClick={() => openTarget
                            ? pinConversationHandler({ conversationId: openTarget.conversationId, isPinned: !openTarget.isPinned })
                            : pinConversationsHandler(true)}
                    >
                        <PinIcon active={!!openTarget?.isPinned} /> {openTarget?.isPinned ? 'Unpin' : 'Pin'}
                    </SecondaryBtn>

                    <SecondaryBtn disabled={!canTag || fetchingConversations} onClick={handleTagConversations}>
                        <Tag size={12} /> Tag
                    </SecondaryBtn>

                    <SecondaryBtn
                        disabled={(hasSelected ? allSelectedRead : !openTarget) || fetchingConversations}
                        onClick={() => openTarget
                            ? readConversationHandler(openTarget, !openTarget.isRead)
                            : readConversationsHandler(true)}
                    >
                        {openTarget?.isRead
                            ? <><Mail size={12} /> Mark as Unread</>
                            : <><MailOpen size={12} /> Mark as Read</>}
                    </SecondaryBtn>

                    <MoreWrapper ref={moreRef}>
                        <SecondaryBtn disabled={!hasSelected || fetchingConversations || isSnTab} onClick={() => setMoreOpen(o => !o)}>
                            <DotsIcon>···</DotsIcon>
                        </SecondaryBtn>
                        {moreOpen && hasSelected && (
                            <MoreDropdown>
                                <MoreItem
                                    disabled={activeInboxLabel === 'snoozed'}
                                    ref={snoozeItemRef}
                                    onClick={() => {
                                        const rect = snoozeItemRef.current?.getBoundingClientRect()
                                        if (rect) setSnoozeAnchor({ top: rect.top, left: rect.right })
                                    }}
                                >
                                    <Clock size={16} /> Snooze
                                </MoreItem>
                                <MoreItem
                                    disabled={activeInboxLabel !== 'snoozed'}
                                    onClick={() => { setUnsnoozeConfirmOpen(true); setMoreOpen(false) }}
                                >
                                    <BellOff size={16} /> Unsnooze
                                </MoreItem>
                                {archived !== 'No' && (
                                    <MoreItem onClick={() => { archiveConversationsHandler(false); setMoreOpen(false) }}>
                                        <ArchiveRestore size={16} /> Unarchive
                                    </MoreItem>
                                )}
                                <MoreItem
                                    disabled={!hasSelected || fetchingConversations || allSelectedUnread}
                                    onClick={() => { readConversationsHandler(false); setMoreOpen(false) }}
                                >
                                    <Mail size={16} /> Mark as Unread
                                </MoreItem>
                                <MoreItem onClick={() => { starConversationsHandler(false); setMoreOpen(false) }}>
                                    <Star size={16} /> Unstar
                                </MoreItem>
                                <MoreItem onClick={() => { pinConversationsHandler(false); setMoreOpen(false) }}>
                                    <PinIcon /> Unpin
                                </MoreItem>
                                <MoreItem onClick={() => { archiveConversationsHandler(true); setMoreOpen(false) }}>
                                    <Archive size={16} /> Archive
                                </MoreItem>
                            </MoreDropdown>
                        )}
                        {snoozeAnchor && (
                            <SnoozeMenu
                                anchor={snoozeAnchor}
                                placement="right"
                                onSnooze={(snoozedUntil) => { snoozeConversationsHandler(snoozedUntil); setSnoozeAnchor(null); setMoreOpen(false) }}
                                onClose={() => setSnoozeAnchor(null)}
                            />
                        )}
                    </MoreWrapper>

                    {unsnoozeConfirmOpen && ReactDOM.createPortal(
                        <UnsnoozeOverlay onClick={() => setUnsnoozeConfirmOpen(false)}>
                            <UnsnoozePanel onClick={e => e.stopPropagation()}>
                                <UnsnoozeTitle>Unsnooze selected chats?</UnsnoozeTitle>
                                <UnsnoozeBody>
                                    {selectedConversations.length} {selectedConversations.length === 1 ? 'chat' : 'chats'} will move back to your inbox and will no longer be snoozed.
                                </UnsnoozeBody>
                                <UnsnoozeActions>
                                    <UnsnoozeCancelBtn onClick={() => setUnsnoozeConfirmOpen(false)}>Cancel</UnsnoozeCancelBtn>
                                    <UnsnoozeConfirmBtn onClick={() => { snoozeConversationsHandler(null); setUnsnoozeConfirmOpen(false) }}>Unsnooze</UnsnoozeConfirmBtn>
                                </UnsnoozeActions>
                            </UnsnoozePanel>
                        </UnsnoozeOverlay>,
                        document.body
                    )}
                </LeftControls>

                <RightControls>
                    <OnboardingTarget>
                        <TooltipWrapper tooltipText="Keyboard shortcuts (?)" placement="bottom">
                            <ShortcutsBtn
                                onClick={onShowShortcuts}
                                disabled={fetchingConversations}
                                $isOnboarding={onboardingStep === 'shortcuts'}
                            >
                                <Keyboard size={18} />
                            </ShortcutsBtn>
                        </TooltipWrapper>
                        {onboardingStep === 'shortcuts' && (
                            <OnboardingBubble>
                                <OnboardingKicker>Quick win</OnboardingKicker>
                                <OnboardingTitle>Shortcut hub</OnboardingTitle>
                                <OnboardingText>
                                    Find all inbox shortcuts here and move faster without digging through menus.
                                </OnboardingText>
                                <OnboardingActions>
                                    <OnboardingGhostBtn onClick={showPreviousOnboardingStep}>Back</OnboardingGhostBtn>
                                    <OnboardingPrimaryBtn onClick={closeOnboarding}>Done</OnboardingPrimaryBtn>
                                </OnboardingActions>
                            </OnboardingBubble>
                        )}
                    </OnboardingTarget>

                    {/* Training reads your own LinkedIn conversations through the extension — personal inbox only. */}
                    {!isSharedInbox && <OnboardingTarget>
                        <TooltipWrapper tooltipText="Train AI by my conversations" placement="bottom">
                            <AiTrainBtn
                                disabled={isTrainingAi || fetchingConversations}
                                onClick={() => onOpenTrainAiModal?.()}
                                $isOnboarding={onboardingStep === 'train-ai'}
                            >
                                {isTrainingAi ? <AiTrainSpinner /> : <Sparkles size={18} />}
                            </AiTrainBtn>
                        </TooltipWrapper>
                        {onboardingStep === 'train-ai' && (
                            <OnboardingBubble>
                                <OnboardingKicker>AI boost</OnboardingKicker>
                                <OnboardingTitle>Train your model</OnboardingTitle>
                                <OnboardingText>
                                    Use this button to train AI on your conversations so message suggestions sound more like you.
                                </OnboardingText>
                                <OnboardingActions>
                                    <OnboardingGhostBtn onClick={closeOnboarding}>Skip</OnboardingGhostBtn>
                                    <OnboardingPrimaryBtn onClick={showNextOnboardingStep}>Next</OnboardingPrimaryBtn>
                                </OnboardingActions>
                            </OnboardingBubble>
                        )}
                    </OnboardingTarget>}

                    <FilterToggleGroup $active={numberOfFilters > 0}>
                        <FilterToggleMain
                            type="button"
                            $segmented={numberOfFilters > 0}
                            onClick={() => setFiltersOpened(s => !s)}
                            disabled={fetchingConversations}
                        >
                            <ListFilter size={18} /> Filters
                            {numberOfFilters > 0 && <FilterBadge>{numberOfFilters}</FilterBadge>}
                            {filtersOpened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </FilterToggleMain>
                        {numberOfFilters > 0 && (
                            <TooltipWrapper tooltipText="Clear all filters" placement="bottom">
                                <FilterToggleClear
                                    type="button"
                                    aria-label="Clear all filters"
                                    disabled={fetchingConversations}
                                    onClick={resetAllFilters}
                                >
                                    <X size={16} strokeWidth={2.25} />
                                </FilterToggleClear>
                            </TooltipWrapper>
                        )}
                    </FilterToggleGroup>
                </RightControls>
            </TopRow>

            {filtersOpened && (
                <BottomRow>
                    <FiltersRow>
                        <StarredFilter disabled={fetchingConversations || isSnTab} />
                        <PinUnpinFilter disabled={fetchingConversations || isSnTab} />
                        <TagsFilter disabled={fetchingConversations} />
                        <ReadUnreadFilter disabled={fetchingConversations || isSnTab} />
                        <ArchiveFilter disabled={fetchingConversations || isSnTab} />
                        <LastMessagedFilter disabled={fetchingConversations || isSnTab} />
                    </FiltersRow>
                </BottomRow>
            )}
        </Container>
    )
}

const accentBlue = '#5964ff'
const iconMuted = '#8c96ad'

const Container = styled.div`
    width: 100%;
    flex-shrink: 0;
    background: #fafafa;
    padding: 12px 16px 15px;
    border-bottom: 1px solid #dae0e8;
    font-family: 'Nunito Sans', sans-serif;
`
const TopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 36px;
    gap: 8px;
`
const LeftControls = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`
const PrimaryBtn = styled.button`
    height: 36px;
    padding: 0 16px 0 14px;
    border-radius: 10px;
    border: none;
    background: #6467f2;
    color: white;
    font-size: 13px;
    font-weight: 700;
    line-height: 13px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:hover:not(:disabled) { background: #5053d4; }
`
const PlusSign = styled.span`
    font-size: 18px;
    font-weight: 400;
    line-height: 1;
    margin-right: -2px;
`
const SecondaryBtn = styled.button`
    height: 36px;
    padding: 0 13px;
    border-radius: 10px;
    border: 1px solid #e8eaed;
    background: #fff;
    color: #6a7181;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;

    svg { width: 14px; height: 14px; flex-shrink: 0; }
    svg.svg { width: 18px; height: 18px; }
    svg .path { fill: ${iconMuted}; }

    &:disabled { opacity: 0.4; cursor: not-allowed; }
    &:hover:not(:disabled) {
        border-color: #c5cdd8;
        color: #41495e;
    }
`
const DotsIcon = styled.span`
    font-size: 16px;
    letter-spacing: 1px;
    line-height: 1;
    color: ${iconMuted};
`
const MoreWrapper = styled.div`
    position: relative;
    flex-shrink: 0;
`
const MoreDropdown = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    background: ${props => props.theme.secondaryColor || '#fff'};
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51, 57, 81, 0.1);
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 206px;
    z-index: 1000;
    overflow: hidden;
`
const MoreItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 6px;
    box-sizing: border-box;
    background: none;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #41495e;
    text-align: left;
    white-space: nowrap;
    transition: background-color 0.15s ease;

    svg { flex-shrink: 0; color: ${accentBlue}; }
    svg .path { fill: ${iconMuted}; }

    &:hover:not(:disabled) {
        background-color: #f3f4f6;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    &:disabled:hover {
        opacity: 0.45;
        background: none;
    }
`
const FilterToggleGroup = styled.div<{ $active?: boolean }>`
    display: inline-flex;
    align-items: stretch;
    flex-shrink: 0;
    border-radius: 10px;
    border: 1px solid ${props => props.$active ? accentBlue : '#e8eaed'};
    background: ${props => props.$active ? '#eef3ff' : '#fff'};
    overflow: hidden;
`
const FilterToggleMain = styled.button<{ $segmented?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: ${props => props.$segmented ? '0 10px 0 12px' : '0 12px'};
    border: none;
    background: transparent;
    color: ${accentBlue};
    font-size: 13px;
    font-weight: 700;
    line-height: 19.5px;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`
const FilterToggleClear = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    min-width: 34px;
    height: 36px;
    padding: 0;
    border: none;
    border-left: 1px solid #dae0e8;
    background: transparent;
    color: ${accentBlue};
    cursor: pointer;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.05);
        color: #ff6760;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`
const FilterBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${accentBlue};
    color: white;
    font-size: 10px;
    font-weight: bold;
`
const BottomRow = styled.div`
    padding-top: 16px;
`
const FiltersRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
`
const RightControls = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`
const OnboardingTarget = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    flex-shrink: 0;
`
const IconBtn = styled.button<{ $isOnboarding?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #e8eaed;
    background: #f6f8fb;
    color: ${iconMuted};
    cursor: pointer;
    flex-shrink: 0;
    position: relative;
    z-index: ${props => props.$isOnboarding ? 1002 : 1};
    transition: border-color 0.15s, background 0.15s, color 0.15s, box-shadow 0.15s;

    &::after {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 9999px;
        padding: 3px;
        background: conic-gradient(
            from 180deg,
            rgba(255,255,255,0) 0%,
            rgba(100,103,242,0.95) 18%,
            rgba(48,215,186,0.85) 32%,
            rgba(255,255,255,0) 48%,
            rgba(255,255,255,0) 100%
        );
        opacity: 0;
        pointer-events: none;
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
    }

    ${props => props.$isOnboarding ? `
        border-color: rgba(100, 103, 242, 0.55);
        background: #eef3ff;
        color: ${accentBlue};
        box-shadow: 0 8px 24px rgba(89, 100, 255, 0.24);

        &::after {
            opacity: 1;
            animation: inbox-onboarding-orbit 1350ms linear infinite;
        }
    ` : ''}

    &:hover:not(:disabled) {
        border-color: #dae0e8;
        background: #eef3ff;
        color: ${accentBlue};
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @keyframes inbox-onboarding-orbit {
        to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
        &::after { animation: none !important; }
    }
`
const ShortcutsBtn = IconBtn
const AiTrainBtn = IconBtn
const OnboardingBubble = styled.div`
    position: absolute;
    top: calc(100% + 14px);
    right: -8px;
    width: 286px;
    padding: 16px;
    border-radius: 18px;
    background:
        radial-gradient(circle at top left, rgba(255,255,255,0.95), rgba(255,255,255,0) 42%),
        linear-gradient(135deg, #ffffff 0%, #f5f7ff 48%, #edf9ff 100%);
    border: 1px solid rgba(100, 103, 242, 0.18);
    box-shadow: 0 18px 42px rgba(30, 41, 59, 0.18), 0 6px 16px rgba(89, 100, 255, 0.12);
    z-index: 1001;
    color: #41495e;

    &::before {
        content: '';
        position: absolute;
        top: -7px;
        right: 21px;
        width: 14px;
        height: 14px;
        background: #ffffff;
        border-left: 1px solid rgba(100, 103, 242, 0.18);
        border-top: 1px solid rgba(100, 103, 242, 0.18);
        transform: rotate(45deg);
    }

    &::after {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 19px;
        padding: 1px;
        background: linear-gradient(115deg, rgba(100,103,242,0), rgba(100,103,242,0.9), rgba(48,215,186,0.75), rgba(100,103,242,0));
        pointer-events: none;
        opacity: 0.85;
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        animation: inbox-onboarding-shimmer 2200ms ease-in-out infinite;
    }

    @keyframes inbox-onboarding-shimmer {
        0%, 100% { opacity: 0.35; }
        50% { opacity: 0.95; }
    }

    @media (prefers-reduced-motion: reduce) {
        &::after { animation: none !important; }
    }
`
const OnboardingKicker = styled.div`
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 9999px;
    background: rgba(100, 103, 242, 0.1);
    color: ${accentBlue};
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`
const OnboardingTitle = styled.div`
    margin-top: 10px;
    font-size: 16px;
    font-weight: 800;
    line-height: 21px;
    color: #20283a;
`
const OnboardingText = styled.div`
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    color: #6a7181;
`
const OnboardingActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;
`
const OnboardingGhostBtn = styled.button`
    height: 30px;
    padding: 0 10px;
    border: none;
    background: transparent;
    color: #8c96ad;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover { color: #5964ff; }
`
const OnboardingPrimaryBtn = styled.button`
    height: 30px;
    padding: 0 14px;
    border: none;
    border-radius: 9999px;
    background: linear-gradient(135deg, #6467f2 0%, #5964ff 55%, #30d7ba 130%);
    color: white;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(89, 100, 255, 0.24);

    &:hover { filter: brightness(0.97); }
`
const AiTrainSpinner = styled.span`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ai-spin 0.7s linear infinite;

    @keyframes ai-spin {
        to { transform: rotate(360deg); }
    }
`

const UnsnoozeOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
`
const UnsnoozePanel = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 28px 32px 24px;
    width: 560px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
    font-family: 'Nunito Sans', sans-serif;
`
const UnsnoozeTitle = styled.h3`
    font-size: 16px;
    font-weight: 800;
    color: #20283a;
    margin: 0 0 10px;
`
const UnsnoozeBody = styled.p`
    font-size: 14px;
    color: #6a7181;
    line-height: 1.55;
    margin: 0 0 24px;
`
const UnsnoozeActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
`
const UnsnoozeCancelBtn = styled.button`
    height: 38px;
    padding: 0 18px;
    border-radius: 10px;
    border: 1px solid #e8eaed;
    background: #fff;
    color: #41495e;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover { border-color: #c5cdd8; }
`
const UnsnoozeConfirmBtn = styled.button`
    height: 38px;
    padding: 0 18px;
    border-radius: 10px;
    border: none;
    background: #6467f2;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    &:hover { background: #5053d4; }
`

export default InboxControlBar
